import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {useAuth} from "../../../context/AuthContext";
import ConversationForm from "../Form";
import {Conversation, ConversationData} from "../../../types/Conversation";
import {HashtagIcon} from "@heroicons/react/outline";
import NotAuthorised from "../../NotAuthorised";
import {DateTime} from "luxon";
import FeatureFlag from "../../../types/FeatureFlag";

function MutateConversation() {
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();
  const queryClient = useQueryClient();
  const canUseAIChatbot = auth.canUse(FeatureFlag.AIChatbot);

  const [question, setQuestion] = useState('');

  const {data: conversation, isLoading} = useQuery(
    ['conversations', {id}],
    () => ngage.conversations.get(Number(id)),
    {enabled: !!id && canUseAIChatbot /*&& auth.can('exhibits:write') */} // TODO: Add conversations permissions
  )

  const store = useMutation((data: ConversationData) => ngage.conversations.store(data));
  const update = useMutation((data: ConversationData) => ngage.conversations.update(Number(id), data));

  // Handle form submission
  async function handleSubmit(values: ConversationData, {setSubmitting, resetForm}: FormikHelpers<ConversationData>) {

    resetForm();
    setQuestion(values.question ?? '');

    try {
      id ? await handleUpdate(values) : await handleStore(values)
    } catch (error) {
    } finally {
      await invalidate('conversations.all', { refetchInactive: true });
      setSubmitting(false);
    }
  }

  // Handle store mutation
  async function handleStore(values: ConversationData) {
    try {
      const response = await store.mutateAsync(values);

      if (response?.id) navigate(`/conversations/${response.id}`);
    } catch (error) {
      notify("error", `Message could not be sent`, `There was an error sending your message!`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: ConversationData) {
    try {
      // Update the conversation in the cache with the user message
      queryClient.setQueryData<Conversation | undefined>(['conversations', {id}], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          messages: [...oldData.messages, {
            id: Date.now(),
            role: 'user',
            content: values.question ?? '',
            tokens_used: 0,
            created_at: DateTime.now(),
          }],
        }
      })

      // Send the update request
      const updateResponse = await update.mutateAsync(values);

      // Get the assistant message from the response
      const updatedAssistantMessage = updateResponse?.messages.find(message => message.role === 'assistant');

      // Update the conversation in the cache with the assistant message
      if (updatedAssistantMessage) {
        queryClient.setQueryData<Conversation | undefined>(['conversations', {id}], (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            messages: [...oldData.messages, updatedAssistantMessage],
          }
        });
      }

      // Invalidate the conversation to refetch updated data
      await invalidate(['conversations', {id}]);
    } catch (error) {
      notify("error", `Message could not be sent`, `There was an error sending your message!`)
      throw error
    }
  }

  // Prevent unauthorised viewing
  // TODO: Add conversations permissions
  if (!canUseAIChatbot /*|| auth.cannot('exhibits:write')*/) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'Conversations': '/conversations',
    'New Conversation': '/conversations/new'
  }
  const editBreadcrumbs = {
    'Conversations': '/conversations',
  }

  // Prepare meta
  const meta = [
    {icon: HashtagIcon, value: `${conversation?.tokens_used?.toLocaleString() ?? 0} tokens used`},
  ]

  return (
    <Page
      title={`${id ? (conversation?.title ?? '...') : 'New Conversation'}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      meta={meta}
      isLoading={isLoading}
    >
      <ConversationForm
        conversation={conversation}
        onSubmit={handleSubmit}
        isLoading={update.isLoading || store.isLoading}
        question={question}
      />
    </Page>
  );
}

export default MutateConversation;